// @flow

import React, { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import type { Shift } from '../../common/typings';
import AnimatedRow from '../../common/components/Forms/AnimatedRow';
import RowIcon from '../../common/components/Forms/RowIcon';
import RowContent from '../../common/components/Forms/RowContent';
import Label from '../../common/components/Text/Label';
import clockNoteIcon from '../../assets/svg/icons/clock-note-gray.svg';
import { addOrEditTimeclockNote } from '../actions';
import ClickableRow from '../../common/components/Forms/ClickableRow';
import DisplayText from '../../common/components/Text/DisplayText';
import { ENTER_KEY_CODE } from '../../constants';

import ViewTimeClockNote from './ViewTimeClockNote';
import StyledTextArea from './StyledTextarea';
import AddNoteButton from './AddNoteButton';

const PaddedAnimatedRow = styled(AnimatedRow)`
  padding-left: 16px;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  flex: 1;
  padding-right: 16px;
  padding-top: 8px;
`;

const SubText = styled(DisplayText)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 16px;
  padding-top: 8px;
`;

type Props = {
  shift: Shift,
  delay: number,
  adHocClockIn?: boolean,
  getNoteForAdHoc: (note: string) => void
};

const TimeClockNote = (
  { shift, delay: firstDelay, adHocClockIn, getNoteForAdHoc }: Props,
  ref: any
) => {
  const initialContent = shift?.assigneeNotes ? shift.assigneeNotes : '';
  const dispatch = useDispatch();
  const firstDelayRef = useRef(true);

  const [note, setNote] = useState(initialContent);
  const [editModeOn, setEditModeOn] = useState(false);
  const [subTextVisible, setSubTextVisible] = useState(false);
  const [addNoteButtonVisible, setAddNoteButtonVisible] = useState(!initialContent);

  const saveNote = () => {
    const trimmedNote = note.trim();
    setEditModeOn(false);
    setAddNoteButtonVisible(!trimmedNote);

    if (adHocClockIn || !shift || initialContent === trimmedNote) {
      return;
    }

    dispatch(addOrEditTimeclockNote(shift.id, trimmedNote));
    setSubTextVisible(false);
    firstDelayRef.current = false;
  };

  const handleOnKeyPress = e => {
    if (e.which === ENTER_KEY_CODE && e.shiftKey === false) {
      e.preventDefault();

      if (adHocClockIn) {
        setEditModeOn(false);
        setAddNoteButtonVisible(!note);
        firstDelayRef.current = false;

        return;
      }

      saveNote();
    }
  };

  const handleChange = e => {
    setNote(e.target.value);
    adHocClockIn && getNoteForAdHoc(e.target.value);
  };

  const handleOnBlur = () => {
    setSubTextVisible(false);
  };

  const handleOnFocus = () => {
    setSubTextVisible(true);
  };

  useImperativeHandle(ref, () => ({ saveNote }));

  const handleEditMode = () => {
    setEditModeOn(true);
    setAddNoteButtonVisible(false);
    firstDelayRef.current = false;
  };

  const delay = firstDelayRef.current ? firstDelay : 0;

  // for some reason react.runtime=automatic option in flow config does not recognize that
  // Fragment shorthand syntax (<>...</>) doesn't need React import anymore
  return (
    <React.Fragment>
      {addNoteButtonVisible && <AddNoteButton delay={delay} onClick={handleEditMode} />}

      {!editModeOn && !addNoteButtonVisible && (
        <ClickableRow onClick={handleEditMode}>
          <ViewTimeClockNote note={shift?.assigneeNotes || note} delay={delay} />
        </ClickableRow>
      )}

      {editModeOn && (
        <PaddedAnimatedRow noPaddingHorizontal borderBottom delay={delay}>
          <RowIcon src={clockNoteIcon} />
          <RowContent>
            <Label>Comment</Label>
            <Container>
              <StyledTextArea
                maxRows={4}
                placeholder="Add a comment"
                value={note}
                onChange={e => handleChange(e)}
                onBlur={handleOnBlur}
                onFocus={handleOnFocus}
                onKeyPress={handleOnKeyPress}
              />
            </Container>
            {subTextVisible && (
              <SubText fontSize="12px" themeColor={'regularTextColor'}>
                Press
                <DisplayText fontSize="12px" italic={true} themeColor={'regularTextColor'} bold>
                  &nbsp;Enter&nbsp;
                </DisplayText>
                to save
              </SubText>
            )}
          </RowContent>
        </PaddedAnimatedRow>
      )}
    </React.Fragment>
  );
};

// $FlowFixMe
export default forwardRef(TimeClockNote);
